/**
 * The viewer's theme and text size: reading them, changing them, and the event that says they
 * changed. See docs/color-themes-plan.md §4.4 and docs/text-size-plan.md §4.4.
 *
 * base.html renders data-theme-pref and data-text-size on <html>, and an inline script in its
 * <head> resolves the preference into data-theme before anything paints. Those attributes are
 * the only copy on the page.
 */
import CurrentUser from 'shared/CurrentUser';
import {GLOBAL_EVENTS, globalEventBus} from 'shared/globalEventBus';
import {setCookie} from 'shared/utils/cookies';
import UserProfileSettingsResource from '../userprofile/userprofile_settings_resource';

const THEMES = ['system', 'light', 'dark', 'high-contrast'];
const TEXT_SIZES = ['small', 'default', 'large', 'larger'];
const WATCHED_ATTRIBUTES = ['data-theme-pref', 'data-theme', 'data-text-size'];

/** The rule the inline script in base.html applies too; that one has to run before this loads */
function resolveTheme (pref, matchMedia = window.matchMedia) {
    if (pref !== 'system') {
        return pref;
    }
    if (matchMedia('(prefers-contrast: more)').matches) {
        return 'high-contrast';
    }
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getDisplayPrefs () {
    const root = document.documentElement;
    return {
        theme: root.getAttribute('data-theme-pref') || 'system',
        resolvedTheme: root.getAttribute('data-theme') || 'light',
        textSize: root.getAttribute('data-text-size') || 'default',
    };
}

// read by core.context_processors.display_prefs when the page is for a signed out visitor
function rememberInCookie (name, value) {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    setCookie(name, value, {expires, path: '/'});
}

/** The fields update_display_prefs takes; throws on a value the server would not take */
function toProfileFields ({theme, textSize} = {}) {
    if (theme !== undefined && !THEMES.includes(theme)) {
        throw new Error(`Unknown theme: ${theme}`);
    }
    if (textSize !== undefined && !TEXT_SIZES.includes(textSize)) {
        throw new Error(`Unknown text size: ${textSize}`);
    }
    const data = {};
    if (theme !== undefined) {
        data.display_theme = theme;
    }
    if (textSize !== undefined) {
        data.text_size = textSize;
    }
    return data;
}

/**
 * Changes the page and the cookie, without saving to the profile.
 *
 * @param prefs {Object} {theme, textSize}, either or both
 * @returns {Object} the profile fields that changed
 */
function applyDisplayPrefs ({theme, textSize} = {}) {
    const data = toProfileFields({theme, textSize});
    const root = document.documentElement;
    if (theme !== undefined) {
        root.setAttribute('data-theme-pref', theme);
        root.setAttribute('data-theme', resolveTheme(theme));
        rememberInCookie('display_theme', theme);
    }
    if (textSize !== undefined) {
        root.setAttribute('data-text-size', textSize);
        rememberInCookie('text_size', textSize);
    }
    return data;
}

const PREF_FOR_FIELD = {display_theme: 'theme', text_size: 'textSize'};

// per profile: what it holds as far as this page knows, which save last picked each field, and
// the save in flight
const profileSaves = new Map();

/**
 * Saves one after another, so the server gets them in the order they were picked. A failed save
 * rejects with {error, revert}: the prefs to put back, leaving out any a later pick changed again.
 */
function saveInOrder (profileId, data, saved) {
    let state = profileSaves.get(profileId);
    if (!state) {
        state = {saved: toProfileFields(saved), latest: {}, count: 0, tail: Promise.resolve()};
        profileSaves.set(profileId, state);
    }
    const ticket = ++state.count;
    const fields = Object.keys(data);
    fields.forEach(field => state.latest[field] = ticket);
    const request = state.tail.then(() => UserProfileSettingsResource().update_display_prefs(profileId, data));
    state.tail = request.catch(() => {});
    return request.then(() => {
        Object.assign(state.saved, data);
    }, error => {
        const revert = {};
        fields
            .filter(field => state.latest[field] === ticket && state.saved[field] !== undefined)
            .forEach(field => revert[PREF_FOR_FIELD[field]] = state.saved[field]);
        throw {error, revert};
    });
}

/**
 * Applies the change to the page at once, then saves it: to the viewer's profile when signed
 * in, and to a cookie either way, so the sign in page after logging out keeps it as well.
 *
 * @param prefs {Object} {theme, textSize}, either or both
 * @returns {Promise} settles once the profile is saved; on failure the page is put back and it
 *     rejects with {error, revert}, see saveInOrder
 */
function setDisplayPrefs ({theme, textSize} = {}) {
    const before = getDisplayPrefs();
    const data = applyDisplayPrefs({theme, textSize});
    const user = new CurrentUser();
    if (!user.is_authenticated() || !Object.keys(data).length) {
        return Promise.resolve();
    }
    return saveInOrder(user.id, data, {theme: before.theme, textSize: before.textSize}).catch(failure => {
        applyDisplayPrefs(failure.revert);
        throw failure;
    });
}

/**
 * Saves them for the profile whose settings are open. The viewer's own change the page at once;
 * a buddy's for the person they support are only saved, as they are not meant to change the page
 * the buddy is looking at.
 *
 * @param profileId {Number}
 * @param prefs {Object} {theme, textSize}, either or both
 * @param saved {Object} {theme, textSize} the profile held when its settings were loaded
 * @returns {Promise} as setDisplayPrefs
 */
function saveDisplayPrefsFor (profileId, {theme, textSize} = {}, saved = {}) {
    if (profileId === new CurrentUser().id) {
        return setDisplayPrefs({theme, textSize});
    }
    return saveInOrder(profileId, toProfileFields({theme, textSize}), saved);
}

/**
 * Emits DISPLAY_CHANGED with getDisplayPrefs() whenever one of the attributes changes, whoever
 * changed it: setDisplayPrefs, or the inline script following the OS while the preference is
 * `system`. Started once, from index.js.
 *
 * @returns {Function} stops watching
 */
function watchDisplayPrefs () {
    const observer = new MutationObserver(records => {
        // the inline script sets data-theme on every OS change, often to the value it had
        const changed = records.some(record => record.oldValue !== record.target.getAttribute(record.attributeName));
        if (changed) {
            globalEventBus.emit(GLOBAL_EVENTS.DISPLAY_CHANGED, getDisplayPrefs());
        }
    });
    observer.observe(document.documentElement, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: WATCHED_ATTRIBUTES,
    });
    return () => observer.disconnect();
}

export {
    THEMES, TEXT_SIZES, resolveTheme, getDisplayPrefs, applyDisplayPrefs, setDisplayPrefs, saveDisplayPrefsFor,
    watchDisplayPrefs,
};
